class AiUploadModal {
  constructor() {
    this.init();
    this.activeFileInput = null;
    this.currentFile = null;
    this.originalFile = null; // Store original file for regeneration
    this.confirmCallback = null;
    this.regenerateCallback = null; // Callback for regeneration
    this.selectedImageUrl = null;
    this.abortController = null; // For canceling requests
    this.progressAnimation = null; // For stopping progress animation
    this.carouselInterval = null; // For carousel auto-play
    this.currentCarouselIndex = 0; // Current carousel index

    // State tracking for analytics
    this.currentState = 'preview'; // 'preview', 'loading', 'selection'
    this.generationStartTime = null; // Track when generation started
    this.generatedImageCount = 0; // Track number of generated images
    this.currentSourceType = null; // Track current generation source type for model_name
    this.couplePreviewFiles = null;
    this.couplePreviewUrls = null;

    // Bind the beforeunload handler so we can remove it later
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  /**
   * Get current model name based on sourceType
   * @returns {string} model name (seeddream or chatgpt_4o)
   */
  getCurrentModelName() {
    if (typeof GtagUtils !== 'undefined' && this.currentSourceType) {
      return GtagUtils.getModelName(this.currentSourceType);
    }
    return 'chatgpt_4o'; // Default
  }

  /**
   * Handle page unload during generation (for analytics)
   */
  handleBeforeUnload() {
    if (this.currentState === 'loading' && typeof GtagUtils !== 'undefined') {
      const stayDuration = this.generationStartTime
        ? Math.round((Date.now() - this.generationStartTime) / 1000)
        : 0;
      const modelName = this.getCurrentModelName();
      GtagUtils.trackGenerationAbandoned(stayDuration, this.generatedImageCount, modelName);
    }
  }

  init() {
    // If modal already exists, don't create it again
    if (document.getElementById('ai-upload-modal')) {
      this.bindElements();
      return;
    }

    // Create and insert modal HTML
    const modalHTML = this.createModalHTML();
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.bindElements();
  }

  bindElements() {
    this.modal = document.getElementById('ai-upload-modal');
    this.ensurePreviewImagesContainer();
    this.previewFile = document.getElementById('ai-upload-modal-preview-file');
    this.closeButton = this.modal.querySelector('.ai-upload-modal-close');
    this.quickButton = this.modal.querySelector('.ai-upload-modal-quick');
    this.betterButton = this.modal.querySelector('.ai-upload-modal-better');
    this.confirmButton = this.modal.querySelector('.ai-upload-modal-confirm');
    this.buttonsContainer = this.modal.querySelector('.ai-upload-modal-buttons');
    this.regenerateButton = this.modal.querySelector('.ai-upload-modal-regenerate');

    this.setupEventListeners();
  }

  ensurePreviewImagesContainer() {
    const previewState = document.getElementById('ai-upload-modal-preview-state');
    if (!previewState) return;

    let container = document.getElementById('ai-upload-modal-preview-images');
    let img1 = document.getElementById('ai-upload-modal-preview-image');
    let img2 = document.getElementById('ai-upload-modal-preview-image-2');

    if (!container) {
      container = document.createElement('div');
      container.id = 'ai-upload-modal-preview-images';
      container.className = 'ai-upload-modal-preview-images';
      previewState.insertBefore(container, previewState.firstChild);
    }

    if (!img1) {
      img1 = document.createElement('img');
      img1.id = 'ai-upload-modal-preview-image';
      img1.className = 'ai-upload-modal-preview-image';
      img1.alt = 'Preview Image';
    }

    if (!img2) {
      img2 = document.createElement('img');
      img2.id = 'ai-upload-modal-preview-image-2';
      img2.className =
        'ai-upload-modal-preview-image ai-upload-modal-preview-image--secondary';
      img2.alt = 'Preview Image 2';
    }

    if (img1.parentElement !== container) {
      container.appendChild(img1);
    }
    if (img2.parentElement !== container) {
      container.appendChild(img2);
    }

    this.previewImagesContainer = container;
    this.previewImage = img1;
    this.previewImage2 = img2;
  }

  setPreviewImageVisible(img, visible) {
    if (!img) return;
    if (visible) {
      img.hidden = false;
      img.style.display = 'block';
    } else {
      img.hidden = true;
      img.style.display = 'none';
      img.removeAttribute('src');
    }
  }

  clearCouplePreview() {
    this.couplePreviewFiles = null;
    this.previewImagesContainer?.classList.remove('is-dual');
    this.setPreviewImageVisible(this.previewImage2, false);
  }

  readFilePreview(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * 生成轮播 HTML
   */
  generateCarouselHTML() {
    // 检查全局变量是否存在
    if (!window.CAROUSEL_IMAGES || !window.CAROUSEL_IMAGES.original || !window.CAROUSEL_IMAGES.generated) {
      console.warn('[AiUploadModal] CAROUSEL_IMAGES not found, using placeholder');
      return '<div class="carousel-placeholder">Loading images...</div>';
    }

    const { original, generated } = window.CAROUSEL_IMAGES;
    const groupCount = Math.min(original.length, generated.length);

    return Array.from({ length: groupCount }, (_, index) => `
      <div class="carousel-group ${index === 0 ? 'active' : ''}" data-carousel-index="${index}">
        <div class="image-card animate-slide-in-left">
          <img src="${original[index]}" alt="Original">
        </div>
        <div class="image-card animate-slide-in-right">
          <img src="${generated[index]}" alt="Generated 3D">
        </div>
      </div>
    `).join('');
  }

  createModalHTML() {
    return `
      <div id="ai-upload-modal" class="ai-upload-modal-overlay">
        <div class="ai-upload-modal">
          <div class="ai-upload-modal-header">
            <div class="ai-upload-modal-title">Preview Upload File</div>
            <button type="button" class="ai-upload-modal-close">×</button>
          </div>
          <div class="ai-upload-modal-content">
            <!-- 预览状态 -->
            <div id="ai-upload-modal-preview-state" class="ai-upload-modal-preview">
              <div id="ai-upload-modal-preview-images" class="ai-upload-modal-preview-images">
                <img id="ai-upload-modal-preview-image" class="ai-upload-modal-preview-image" src="" alt="Preview Image" hidden>
                <img id="ai-upload-modal-preview-image-2" class="ai-upload-modal-preview-image ai-upload-modal-preview-image--secondary" src="" alt="Preview Image 2" hidden>
              </div>
              <div id="ai-upload-modal-preview-file" class="ai-upload-modal-preview-file">
                <div class="file-icon">📄</div>
                <div class="file-info">
                  <div class="file-name"></div>
                  <div class="file-size"></div>
                </div>
              </div>
            </div>

            <!-- 进度条状态 -->
            <div id="ai-upload-modal-progress-state" class="ai-upload-modal-progress-state" style="display: none;">
              <!-- 轮播动画 -->
              <div class="carousel-container">
                ${this.generateCarouselHTML()}
              </div>

              <h3 class="progress-title">Please wait a moment...</h3>
              <div class="progress-bar-container">
                <div class="progress-bar" id="ai-upload-modal-progress-bar"></div>
              </div>
              <div class="progress-text" id="ai-upload-modal-progress-text"></div>
            </div>

            <!-- 图片选择状态 -->
            <div id="ai-upload-modal-selection-state" class="ai-upload-modal-selection-state" style="display: none;">
              <!-- 多人提示 -->
              <div class="ai-upload-modal-info-notice">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10" cy="10" r="9" stroke="#3b82f6" stroke-width="1.5" fill="none"/>
                  <path d="M10 6V10M10 14H10.01" stroke="#3b82f6" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span>If there are multiple people in your photo, we will contact you to select one.</span>
              </div>
              <div class="ai-upload-modal-image-grid" id="ai-upload-modal-images-grid">
                <!-- 动态插入图片 -->
              </div>
            </div>
          </div>
          <div class="ai-upload-modal-buttons">
            <button type="button" class="ai-upload-modal-regenerate" style="display: none;">Regenerate</button>
            <button type="button" class="ai-upload-modal-generate ai-upload-modal-quick" data-source-type="cute_single_img2img_deliver3d">
              <span class="btn-name-row">
                <svg class="btn-icon" xmlns="http://www.w3.org/2000/svg" fill="none" width="14" height="19" viewBox="0 0 13.750041961669922 18.79157066345215"><path d="M4.3778415,10.997938L0.68825829,10.997938C0.20655009,10.997938,-0.12574175,10.515771,0.04567495,10.065917L3.7118838,0.44275004C3.8134258,0.17620558,4.0690064,0.00004376471,4.3542376,0L10.540821,0C11.028946,0,11.361696,0.4945417,11.177446,0.94668758L9.0427599,6.1863546L13.061426,6.1863546C13.652447,6.1863546,13.96778,6.8830214,13.577968,7.3271461L3.7251763,18.55402C3.246218,19.099895,2.358197,18.626896,2.5440512,17.924957L4.3778415,10.997938ZM4.8279252,1.3750001L1.6855921,9.623167L5.2706757,9.623167C5.7217689,9.6232109,6.0505896,10.050337,5.9352593,10.486439L4.6219044,15.448354L11.543654,7.5611258L8.0204458,7.5611258C7.5323219,7.5611258,7.1995707,7.0665836,7.3838205,6.6144385L9.518508,1.3747717L4.8279252,1.3747717L4.8279252,1.3750001Z" fill="currentColor"/></svg>
                <span class="btn-name">Quick Generate</span>
              </span>
              <span class="btn-time">About 25 seconds</span>
            </button>
            <button type="button" class="ai-upload-modal-generate ai-upload-modal-better" data-source-type="cute_single_img2img_deliver3d_aws">
              <span class="btn-name-row">
                <svg class="btn-icon" xmlns="http://www.w3.org/2000/svg" fill="none" width="20" height="20" viewBox="0 0 19.548009872436523 19.518518447875977"><path d="M9.7740049,19.518518C9.5728226,19.518518,9.380023,19.485039,9.1872225,19.40971C8.7261801,19.242311,8.3657274,18.882404,8.1980753,18.42206L6.3036075,13.224346L1.0980116,11.332747C0.67049897,11.173719,0.33519483,10.864032,0.15077758,10.453906C-0.033639658,10.043781,-0.050404929,9.5918055,0.10886456,9.1733103C0.27651662,8.712966,0.63696855,8.3530588,1.0980116,8.1856613L6.3036075,6.2940617L8.1980753,1.0963489C8.3573446,0.6694836,8.6675014,0.3346872,9.078249,0.15054926C9.4889956,-0.033588719,9.9416571,-0.050328601,10.360787,0.10869971C10.82183,0.27609786,11.182282,0.63600397,11.349935,1.0963489L13.244404,6.2940617L18.449999,8.1856613C18.877512,8.3446894,19.212816,8.6543751,19.397232,9.0645008C19.58165,9.4746265,19.598413,9.9266024,19.439146,10.345098C19.271494,10.805443,18.911041,11.165348,18.449999,11.332747L13.244401,13.224346L11.349933,18.42206C11.190664,18.848925,10.880507,19.183722,10.46976,19.367859C10.251811,19.4683,10.0171,19.518518,9.7740049,19.518518ZM2.6655583,9.7675753L7.1418686,11.391337C7.602911,11.558736,7.9801283,11.935381,8.1477804,12.395726L9.7740049,16.865257L11.400229,12.395726C11.567882,11.935381,11.945098,11.558736,12.406141,11.391337L16.88245,9.7675743L12.406141,8.1438131C11.945098,7.9764142,11.567882,7.5997686,11.400229,7.1394234L9.7740049,2.6698935L8.1477795,7.1394243C7.9801278,7.5997696,7.6029105,7.9764161,7.1418681,8.1438141L2.6655583,9.7675753Z" fill="currentColor"/></svg>
                <span class="btn-name">Better Generate</span>
              </span>
              <span class="btn-time">About 45 seconds</span>
            </button>
            <button type="button" class="ai-upload-modal-confirm" style="display: none;">Confirm Selection</button>
          </div>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    // Listen for all file input changes (backward compatibility)
    document.addEventListener('change', (e) => {
      if (e.target.matches('.custom-file-input')) {
        this.handleFileSelect(e);
      }
    });

    // Close button
    this.closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    // Quick Generate button
    this.quickButton.addEventListener('click', (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      const sourceType = this.quickButton.dataset.sourceType;
      setTimeout(() => {
        this.confirm(sourceType);
      }, 0);
    }, true);

    // Better Generate button
    this.betterButton.addEventListener('click', (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      const sourceType = this.betterButton.dataset.sourceType;
      setTimeout(() => {
        this.confirm(sourceType);
      }, 0);
    }, true);

    // Confirm Selection button (for image selection state)
    this.confirmButton.addEventListener('click', (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      setTimeout(() => {
        this.confirm();
      }, 0);
    }, true);

    // Regenerate button
    this.regenerateButton.addEventListener('click', (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      this.regenerate();
    }, true);

    // Close when clicking overlay
    // this.modal.addEventListener('click', (e) => {
    //   e.stopPropagation(); // Prevent event bubbling to drawer and popover
    //   if (e.target === this.modal) {
    //     this.close();
    //   }
    // });
  }

  handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) return;

    this.activeFileInput = event.target;
    this.currentFile = file;

    // Check if file is an image - by MIME type or file extension
    const fileName = file.name.toLowerCase();
    const isImage = file.type.startsWith('image/') ||
                    fileName.endsWith('.jpg') ||
                    fileName.endsWith('.jpeg') ||
                    fileName.endsWith('.png') ||
                    fileName.endsWith('.webp') ||
                    fileName.endsWith('.heic') ||
                    fileName.endsWith('.heif') ||
                    fileName.endsWith('.gif');

    if (isImage) {
      this.showImagePreview(file);
    } else {
      this.showFilePreview(file);
    }

    this.open();
  }

  /**
   * 显示弹窗并预览文件（新的公共 API）
   * @param {File} file - 要预览的文件对象
   * @param {Function} onConfirm - 用户点击确认后的回调函数，接收文件作为参数
   * @param {Function} [onRegenerate] - 可选，用户点击重新生成后的回调函数
   * @param {{ coupleFiles?: File[], couplePreviewUrls?: string[] }} [options]
   */
  show(file, onConfirm, onRegenerate, options = {}) {
    if (!file) {
      return;
    }

    // 在打开模态框之前锁定抽屉和弹出框，防止被关闭
    const drawer =
      document.querySelector('quick-add-dialog dialog[open]') ||
      document.querySelector('quick-add-dialog');
    const popover = document.querySelector('quick-buy-popover[open]');
    const cartDrawer = document.querySelector('cart-drawer[open]');

    if (drawer) {
      drawer.dataset.modalOpen = 'true';
    }
    if (popover) {
      popover.dataset.modalOpen = 'true';
    }
    if (cartDrawer) {
      cartDrawer.dataset.modalOpen = 'true';
      console.log('[AiUploadModal] Cart drawer locked');
    }

    this.currentFile = file;
    this.originalFile = file; // Store original file for regeneration
    this.confirmCallback = onConfirm;
    this.regenerateCallback = onRegenerate || onConfirm; // Use onConfirm as fallback

    const coupleFiles = Array.isArray(options.coupleFiles)
      ? options.coupleFiles.filter(Boolean)
      : null;
    const couplePreviewUrls = Array.isArray(options.couplePreviewUrls)
      ? options.couplePreviewUrls.filter(
          (url) =>
            typeof url === 'string' &&
            (url.startsWith('blob:') || url.startsWith('data:'))
        )
      : null;

    this.resetToPreview();

    if (coupleFiles?.length >= 2 || couplePreviewUrls?.length >= 2) {
      this.couplePreviewFiles = coupleFiles;
      this.couplePreviewUrls = couplePreviewUrls;
      this.showCoupleImagePreview(
        coupleFiles?.[0],
        coupleFiles?.[1],
        couplePreviewUrls || []
      );
      this.open();
      return;
    }

    this.couplePreviewFiles = null;
    this.couplePreviewUrls = null;

    // 检查是否是图片 - 通过 MIME 类型或文件扩展名
    const fileName = file.name.toLowerCase();
    const isImage = file.type.startsWith('image/') ||
                    fileName.endsWith('.jpg') ||
                    fileName.endsWith('.jpeg') ||
                    fileName.endsWith('.png') ||
                    fileName.endsWith('.webp') ||
                    fileName.endsWith('.heic') ||
                    fileName.endsWith('.heif') ||
                    fileName.endsWith('.gif');

    if (isImage) {
      this.showImagePreview(file);
    } else {
      this.showFilePreview(file);
    }

    this.open();
  }

  showImagePreview(file) {
    this.ensurePreviewImagesContainer();
    this.clearCouplePreview();

    this.readFilePreview(file).then((dataUrl) => {
      this.previewImage.src = dataUrl;
      this.setPreviewImageVisible(this.previewImage, true);
      this.previewFile.style.display = 'none';
    });
  }

  async resolveCouplePreviewSource(file, fallbackUrl) {
    if (file) {
      try {
        return await this.readFilePreview(file);
      } catch (error) {
        console.warn('[AiUploadModal] Failed to read couple preview file:', error);
      }
    }

    if (
      typeof fallbackUrl === 'string' &&
      (fallbackUrl.startsWith('blob:') || fallbackUrl.startsWith('data:'))
    ) {
      return fallbackUrl;
    }

    return '';
  }

  showCoupleImagePreview(file1, file2, previewUrls = []) {
    this.ensurePreviewImagesContainer();

    const img1 = this.previewImage;
    const img2 = this.previewImage2;
    const container = this.previewImagesContainer;

    this.couplePreviewFiles = [file1, file2].filter(Boolean);
    container?.classList.add('is-dual');
    if (this.previewFile) {
      this.previewFile.style.display = 'none';
    }

    const applyUrl = (img, url) => {
      if (
        !img ||
        typeof url !== 'string' ||
        (!url.startsWith('blob:') && !url.startsWith('data:'))
      ) {
        return;
      }
      img.src = url;
      this.setPreviewImageVisible(img, true);
    };

    applyUrl(img1, previewUrls[0]);
    applyUrl(img2, previewUrls[1]);

    Promise.all([
      this.resolveCouplePreviewSource(file1, previewUrls[0]),
      this.resolveCouplePreviewSource(file2, previewUrls[1]),
    ]).then(([dataUrl1, dataUrl2]) => {
      if (dataUrl1) {
        img1.src = dataUrl1;
        this.setPreviewImageVisible(img1, true);
      }
      if (dataUrl2) {
        img2.src = dataUrl2;
        this.setPreviewImageVisible(img2, true);
      }
    });
  }

  showFilePreview(file) {
    this.clearCouplePreview();
    const fileInfo = this.previewFile.querySelector('.file-info');
    fileInfo.querySelector('.file-name').textContent = file.name;
    fileInfo.querySelector('.file-size').textContent = this.formatFileSize(file.size);

    this.setPreviewImageVisible(this.previewImage, false);
    this.setPreviewImageVisible(this.previewImage2, false);
    this.previewFile.style.display = 'flex';
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  open() {
    // Calculate scrollbar width before hiding it
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Add padding to compensate for scrollbar removal
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      // Find likely fixed/sticky positioned elements (performance optimized)
      const potentialElements = document.querySelectorAll(
        'header, nav, .header, .shopify-section, [class*="sticky"], [class*="fixed"], [id*="shopify-section"]'
      );

      potentialElements.forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        const position = computedStyle.position;

        if (position === 'fixed' || position === 'sticky' || position === '-webkit-sticky') {
          const currentPaddingRight = computedStyle.paddingRight;
          const currentPaddingValue = parseFloat(currentPaddingRight) || 0;
          el.style.paddingRight = `${currentPaddingValue + scrollbarWidth}px`;
          el.setAttribute('data-modal-original-padding', currentPaddingRight);
        }
      });
    }

    document.body.style.overflow = 'hidden';
    this.modal.classList.add('active');
  }

  close() {
    // Track analytics event based on current state
    if (typeof GtagUtils !== 'undefined') {
      const fileName = this.currentFile ? this.currentFile.name : '';

      switch (this.currentState) {
        case 'preview':
          // User closed the preview without confirming upload
          GtagUtils.trackUploadPreviewClose(fileName);
          break;
        case 'loading':
          // User closed during generation
          const progressPercentage = this.progressAnimation
            ? Math.round(this.progressAnimation.getCurrentProgress())
            : 0;
          GtagUtils.trackGenerateLoadingClose(progressPercentage);
          break;
        case 'selection':
          // User closed design selection without confirming
          GtagUtils.trackDesignSelectClose(this.generatedImageCount);
          break;
      }
    }

    // Unlock all drawers and popovers (there may be multiple)
    const allDrawers = document.querySelectorAll('quick-add-dialog');
    const allPopovers = document.querySelectorAll('quick-buy-popover');
    allDrawers.forEach(drawer => {
      drawer.dataset.modalOpen = 'false';
    });
    allPopovers.forEach(popover => {
      popover.dataset.modalOpen = 'false';
    });

    const drawer =
      document.querySelector('quick-add-dialog dialog[open]') ||
      document.querySelector('quick-add-dialog');
    const popover = document.querySelector('quick-buy-popover[open]');

    // Cancel ongoing requests
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Stop progress animation
    if (this.progressAnimation) {
      this.progressAnimation.stop();
      this.progressAnimation = null;
    }

    // Stop carousel animation
    this.stopCarousel();

    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    this.modal.classList.remove('active');

    // Check if in drawer or popover - if in either, don't restore body overflow
    if (!drawer && !popover) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      // Restore padding for fixed/sticky elements
      const elementsWithPadding = document.querySelectorAll('[data-modal-original-padding]');
      elementsWithPadding.forEach(el => {
        el.style.paddingRight = el.getAttribute('data-modal-original-padding');
        el.removeAttribute('data-modal-original-padding');
      });
    }

    if (this.activeFileInput) {
      this.activeFileInput.value = '';
    }
    this.activeFileInput = null;
    this.currentFile = null;
    this.originalFile = null;
    this.confirmCallback = null;
    this.regenerateCallback = null;
    this.selectedImageUrl = null;

    // Reset analytics state
    this.currentState = 'preview';
    this.generationStartTime = null;
    this.generatedImageCount = 0;
    this.currentSourceType = null;

    this.resetToPreview();
    document.body.style.overflow = 'unset';
  }

  confirm(sourceType) {
    // Store sourceType for model_name tracking (only when starting generation)
    if (sourceType) {
      this.currentSourceType = sourceType;
    }

    // Track analytics event based on current state
    if (typeof GtagUtils !== 'undefined') {
      if (this.currentState === 'preview' && this.currentFile) {
        // User confirmed upload from preview state
        GtagUtils.trackUploadConfirm(this.currentFile.name, this.currentFile.size);
      } else if (this.currentState === 'selection' && this.selectedImageUrl) {
        // User confirmed design selection
        GtagUtils.trackDesignConfirm(this.selectedImageUrl);
      }
    }

    if (this.confirmCallback) {
      // 调用确认回调，传递文件和 sourceType
      // 注意：不再自动关闭弹窗，由回调函数控制
      this.confirmCallback(this.currentFile, sourceType);
    } else if (this.currentFile) {
      // 向后兼容：如果没有回调，只是关闭弹窗
      this.close();
    }
  }

  /**
   * Regenerate
   */
  regenerate() {
    // Track regenerate click event with current model_name
    if (typeof GtagUtils !== 'undefined') {
      const modelName = this.getCurrentModelName();
      GtagUtils.trackDesignRegenerateClick(modelName, this.generatedImageCount);
    }

    // Reset to progress state and call regenerate callback with original file
    if (this.regenerateCallback && this.originalFile) {
      // Reset state
      this.selectedImageUrl = null;
      this.generatedImageCount = 0;

      // Hide regenerate button during generation
      if (this.regenerateButton) {
        this.regenerateButton.style.display = 'none';
      }

      // Show progress state
      this.showProgress();

      const progressDuration =
        this.currentSourceType === "cute_single_img2img_deliver3d"
          ? 27000
          : 55000;
      // Start progress animation
      this.progressAnimation = this.startProgressAnimation(progressDuration);

      // Call the regenerate callback with the original file
      this.regenerateCallback(this.originalFile);
    }
  }

  /**
   * 切换到进度条状态
   */
  showProgress() {
    // Update analytics state
    this.currentState = 'loading';
    this.generationStartTime = Date.now();

    const previewState = document.getElementById('ai-upload-modal-preview-state');
    const progressState = document.getElementById('ai-upload-modal-progress-state');
    const selectionState = document.getElementById('ai-upload-modal-selection-state');
    const title = this.modal.querySelector('.ai-upload-modal-title');

    if (previewState) previewState.style.display = 'none';
    if (progressState) progressState.style.display = 'block';
    if (selectionState) selectionState.style.display = 'none';
    if (title) title.textContent = 'Generating your preview';

    // Hide all buttons during progress
    if (this.buttonsContainer) this.buttonsContainer.style.display = 'none';

    // 初始化进度条为 1%
    this.updateProgress(1);

    // 启动轮播动画
    this.startCarousel();

    // 创建新的 AbortController
    this.abortController = new AbortController();

    // Add beforeunload listener to track generation abandonment
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * 更新进度
   * @param {number} percentage - 进度百分比 (1-100)
   */
  updateProgress(percentage) {
    const progressBar = document.getElementById('ai-upload-modal-progress-bar');
    const progressText = document.getElementById('ai-upload-modal-progress-text');

    if (progressBar) {
      progressBar.style.width = percentage + '%';
    }
    if (progressText) {
      progressText.textContent = `${Math.round(percentage)}%`;
    }
  }

  /**
   * 启动渐进式进度条（使用缓动函数自然增长到99%）
   * @param {number} durationMs - 到达99%所需的时间（毫秒），默认55秒
   * @returns {Object} 包含 stop 方法的对象
   */
  startProgressAnimation(durationMs = 55000) {
    let progress = 1;
    let animationId;
    const targetPercent = 99;
    const duration = durationMs;
    const startTime = Date.now();

    const animate = () => {
      if (progress < targetPercent) {
        const elapsed = Date.now() - startTime;
        const timeProgress = Math.min(elapsed / duration, 1); // 0-1之间

        // 使用缓动函数（easeOutCubic）使动画更自然
        // 开始快，逐渐变慢
        const easedProgress = 1 - Math.pow(1 - timeProgress, 2);

        // 计算当前进度：从1%到96%
        progress = 1 + (targetPercent - 1) * easedProgress;

        this.updateProgress(progress);

        // 如果还未到达目标时间，继续动画
        if (timeProgress < 1) {
          animationId = requestAnimationFrame(animate);
        }
      }
    };

    animate();

    const progressAnimationObj = {
      stop: () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      },
      getCurrentProgress: () => progress
    };

    // 保存到实例变量
    this.progressAnimation = progressAnimationObj;

    return progressAnimationObj;
  }

  /**
   * 初始化图片选择状态（显示4个loading占位符）
   */
  initImageSelection() {
    // Update analytics state
    this.currentState = 'selection';
    this.generatedImageCount = 0; // Will be updated as images load

    // Remove beforeunload listener since generation phase is complete
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    const previewState = document.getElementById('ai-upload-modal-preview-state');
    const progressState = document.getElementById('ai-upload-modal-progress-state');
    const selectionState = document.getElementById('ai-upload-modal-selection-state');
    const imagesGrid = document.getElementById('ai-upload-modal-images-grid');
    const title = this.modal.querySelector('.ai-upload-modal-title');

    // 停止轮播动画
    this.stopCarousel();

    if (previewState) previewState.style.display = 'none';
    if (progressState) progressState.style.display = 'none';
    if (selectionState) selectionState.style.display = 'block';
    if (title) title.textContent = 'Select Your Design';

    // Show buttons container, hide generate buttons, show confirm button
    if (this.buttonsContainer) this.buttonsContainer.style.display = 'flex';
    if (this.quickButton) this.quickButton.style.display = 'none';
    if (this.betterButton) this.betterButton.style.display = 'none';
    if (this.confirmButton) {
      this.confirmButton.style.display = 'inline-block';
      this.confirmButton.textContent = 'Confirm Selection';
      this.confirmButton.disabled = true; // 初始时禁用，直到有图片加载完成
    }

    // Show regenerate button in selection state
    if (this.regenerateButton) {
      this.regenerateButton.style.display = 'inline-block';
    }

    const jobNum = (window.AI_UPLOAD_CONFIG && window.AI_UPLOAD_CONFIG.jobNum) || 1;

    if (imagesGrid) {
      imagesGrid.innerHTML = Array.from({length: jobNum}, (_, index) => `
        <div class="image-option" data-index="${index}" data-url="">
          <div class="image-loading-spinner"></div>
        </div>
      `).join('');
    }
  }

  /**
   * 更新单个图片槽位
   * @param {number} index - 图片索引 (0-3)
   * @param {string} url - 图片URL
   */
  updateImageSlot(index, url) {
    const imagesGrid = document.getElementById('ai-upload-modal-images-grid');
    if (!imagesGrid) return;

    const imageOption = imagesGrid.querySelector(`[data-index="${index}"]`);
    if (!imageOption) return;

    // Increment generated image count for analytics
    this.generatedImageCount++;

    // Update session tracking data for order_placed event
    if (typeof GtagTrackingHelpers !== 'undefined') {
      const modelName = this.getCurrentModelName();
      GtagTrackingHelpers.incrementGenerationCount(1, modelName);
    }

    // 更新为实际图片
    imageOption.classList.remove('loading');
    imageOption.setAttribute('data-url', url);
    imageOption.innerHTML = `
      <img src="${url}" alt="Option ${index + 1}" loading="lazy" />
      <div class="image-option-overlay">
        <span class="checkmark">✓</span>
      </div>
    `;

    // 如果是第一张完成的图片，自动选中它
    const allOptions = imagesGrid.querySelectorAll('.image-option');
    const hasSelected = Array.from(allOptions).some(opt => opt.classList.contains('selected'));

    if (!hasSelected) {
      imageOption.classList.add('selected');
      this.selectedImageUrl = url;

      // 启用确认按钮
      if (this.confirmButton) {
        this.confirmButton.disabled = false;
      }
    }

    // Add click event
    imageOption.addEventListener('click', () => {
      allOptions.forEach(opt => opt.classList.remove('selected'));
      imageOption.classList.add('selected');
      this.selectedImageUrl = url;
    });
  }

  /**
   * Update image slot to show failed state
   * @param {number} index - Image index (0-3)
   */
  updateImageSlotFailed(index) {
    const imagesGrid = document.getElementById('ai-upload-modal-images-grid');
    if (!imagesGrid) return;

    const imageOption = imagesGrid.querySelector(`[data-index="${index}"]`);
    if (!imageOption) return;

    // Update to show failed state
    imageOption.classList.remove('loading');
    imageOption.classList.add('failed');
    imageOption.setAttribute('data-url', '');
    imageOption.innerHTML = `
      <div class="image-failed-state">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="22" stroke="#ef4444" stroke-width="2" fill="none"/>
          <path d="M16 16L32 32M32 16L16 32" stroke="#ef4444" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span class="failed-text">Failed</span>
      </div>
    `;

    // Make it non-clickable
    imageOption.style.cursor = 'not-allowed';
    imageOption.style.opacity = '0.6';
    imageOption.style.display = 'none';
  }

  /**
   * 切换到图片选择状态（兼容旧方法）
   * @param {Array} images - 图片数组 [{url, jobId}, ...]
   */
  showImageSelection(images) {
    const previewState = document.getElementById('ai-upload-modal-preview-state');
    const progressState = document.getElementById('ai-upload-modal-progress-state');
    const selectionState = document.getElementById('ai-upload-modal-selection-state');
    const imagesGrid = document.getElementById('ai-upload-modal-images-grid');
    const title = this.modal.querySelector('.ai-upload-modal-title');

    if (previewState) previewState.style.display = 'none';
    if (progressState) progressState.style.display = 'none';
    if (selectionState) selectionState.style.display = 'block';
    if (title) title.textContent = 'Select Your Design';

    // Show buttons container, hide generate buttons, show confirm button
    if (this.buttonsContainer) this.buttonsContainer.style.display = 'flex';
    if (this.quickButton) this.quickButton.style.display = 'none';
    if (this.betterButton) this.betterButton.style.display = 'none';
    if (this.confirmButton) {
      this.confirmButton.style.display = 'inline-block';
      this.confirmButton.textContent = 'Confirm Selection';
    }

    // 生成图片网格
    if (imagesGrid) {
      imagesGrid.innerHTML = images.map((img, index) => `
        <div class="image-option ${index === 0 ? 'selected' : ''}" data-index="${index}" data-url="${img.url}">
          <img src="${img.url}" alt="Option ${index + 1}" loading="lazy" />
          <div class="image-option-overlay">
            <span class="checkmark">✓</span>
          </div>
        </div>
      `).join('');

      // 默认选中第一张
      this.selectedImageUrl = images[0]?.url || '';

      // 设置选择事件
      const imageOptions = imagesGrid.querySelectorAll('.image-option');
      imageOptions.forEach(option => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          imageOptions.forEach(opt => opt.classList.remove('selected'));
          option.classList.add('selected');
          this.selectedImageUrl = option.getAttribute('data-url');
        });
      });
    }
  }

  /**
   * 获取当前选中的图片 URL
   */
  getSelectedImageUrl() {
    return this.selectedImageUrl;
  }

  /**
   * 重置到预览状态
   */
  resetToPreview() {
    this.ensurePreviewImagesContainer();
    const previewState = document.getElementById('ai-upload-modal-preview-state');
    const progressState = document.getElementById('ai-upload-modal-progress-state');
    const selectionState = document.getElementById('ai-upload-modal-selection-state');
    const errorState = document.getElementById('ai-upload-modal-error-state');
    const title = this.modal.querySelector('.ai-upload-modal-title');

    // 停止轮播动画
    this.stopCarousel();

    if (previewState) previewState.style.display = 'block';
    if (progressState) progressState.style.display = 'none';
    if (selectionState) selectionState.style.display = 'none';
    if (errorState) errorState.style.display = 'none'; // 隐藏错误状态
    if (title) title.textContent = 'Preview Upload File';

    // Show buttons container, show generate buttons, hide confirm button
    if (this.buttonsContainer) this.buttonsContainer.style.display = 'flex';
    if (this.quickButton) this.quickButton.style.display = 'inline-flex';
    if (this.betterButton) this.betterButton.style.display = 'inline-flex';
    if (this.confirmButton) {
      this.confirmButton.style.display = 'none';
      this.confirmButton.disabled = false;
    }

    // Hide regenerate button in preview state
    if (this.regenerateButton) {
      this.regenerateButton.style.display = 'none';
    }

    // 清除图片选择网格（清除之前失败或成功的图片状态）
    const imagesGrid = document.getElementById('ai-upload-modal-images-grid');
    if (imagesGrid) {
      imagesGrid.innerHTML = '';
      console.log('[AiUploadModal] Cleared image grid');
    }

    // 重置选中的图片URL
    this.selectedImageUrl = null;
    this.couplePreviewUrls = null;
    this.clearCouplePreview();
    this.setPreviewImageVisible(this.previewImage, false);
  }

  /**
   * 显示通用错误（弹框仅展示 Upload Error / Upload Failed 等友好文案，具体报错仅打控制台）
   * @param {string} title - 错误标题
   * @param {string} message - 错误消息（可能含 API 详情，不直接展示）
   * @param {string} details - 错误详情（可选，不展示）
   */
  showError(title, message, details) {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.progressAnimation) {
      this.progressAnimation.stop();
      this.progressAnimation = null;
    }

    this.stopCarousel();

    const logParts = [title, message, details].filter(Boolean);
    console.error(
      '[AiUploadModal]',
      logParts.length ? logParts.join(' — ') : 'Upload failed'
    );

    const previewState = document.getElementById('ai-upload-modal-preview-state');
    const progressState = document.getElementById('ai-upload-modal-progress-state');
    const selectionState = document.getElementById('ai-upload-modal-selection-state');

    if (previewState) previewState.style.display = 'none';
    if (progressState) progressState.style.display = 'none';
    if (selectionState) selectionState.style.display = 'none';

    const displayTitle = this.getFriendlyErrorTitle(title);
    const displayMessage = this.getFriendlyErrorMessage(title, message);

    let errorState = document.getElementById('ai-upload-modal-error-state');
    if (!errorState) {
      errorState = document.createElement('div');
      errorState.id = 'ai-upload-modal-error-state';
      errorState.className = 'upload-error-state';
      const modalContent = this.modal.querySelector('.ai-upload-modal-content');
      modalContent.appendChild(errorState);
    }

    errorState.innerHTML = `
      <div class="error-icon">⚠️</div>
      <h3 class="error-title">${displayTitle}</h3>
      <p class="error-message">${displayMessage}</p>
    `;
    errorState.style.display = 'block';

    const modalTitle = this.modal.querySelector('.ai-upload-modal-title');
    if (modalTitle) modalTitle.textContent = 'Upload Error';

    if (this.buttonsContainer) this.buttonsContainer.style.display = 'none';
  }

  getFriendlyErrorTitle(title) {
    const normalized = (title || '').trim();
    const knownTitles = {
      'Daily Limit Reached': 'Daily Limit Reached',
      'Network Error': 'Network Error',
      'General error': 'General Error',
      'Upload Failed': 'Upload Failed',
    };
    return knownTitles[normalized] || 'Upload Failed';
  }

  getFriendlyErrorMessage(title, message) {
    const normalizedTitle = (title || '').trim();
    const normalizedMessage = (message || '').trim();
    const allowedMessages = new Set([
      "You've reached today's limit of 10 AI previews. Please try again tomorrow.",
      'Please wait a moment and try again.',
      'Poor network connection. Please try again later.',
      'Please try again with a different photo.',
      'An error occurred while uploading your image. Please try again.',
    ]);

    if (allowedMessages.has(normalizedMessage)) {
      return normalizedMessage;
    }

    if (normalizedTitle === 'Daily Limit Reached') {
      return "You've reached today's limit of 10 AI previews. Please try again tomorrow.";
    }
    if (normalizedTitle === 'Network Error') {
      return 'Poor network connection. Please try again later.';
    }
    if (normalizedTitle === 'General error') {
      return 'Please wait a moment and try again.';
    }

    return 'An error occurred while uploading your image. Please try again.';
  }

  /**
   * 启动轮播动画
   */
  startCarousel() {
    // 重置轮播索引
    this.currentCarouselIndex = 0;

    // 确保第一个轮播组是可见的
    this.updateCarousel();

    // 启动自动轮播（每4秒切换一次）
    this.carouselInterval = setInterval(() => {
      const carouselGroups = document.querySelectorAll('.carousel-group');
      this.currentCarouselIndex = (this.currentCarouselIndex + 1) % carouselGroups.length;
      this.updateCarousel();
    }, 4000);
  }

  /**
   * 停止轮播动画
   */
  stopCarousel() {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.carouselInterval = null;
    }
  }

  /**
   * 更新轮播显示状态
   */
  updateCarousel() {
    const carouselGroups = document.querySelectorAll('.carousel-group');

    carouselGroups.forEach((group, index) => {
      if (index === this.currentCarouselIndex) {
        group.classList.add('active');

        // 重新触发动画
        const leftCards = group.querySelectorAll('.animate-slide-in-left');
        const rightCards = group.querySelectorAll('.animate-slide-in-right');

        // 移除动画类
        leftCards.forEach(card => {
          card.classList.remove('animate-slide-in-left');
        });
        rightCards.forEach(card => {
          card.classList.remove('animate-slide-in-right');
        });

        // 强制重排以重新触发动画
        void group.offsetWidth;

        // 重新添加动画类
        leftCards.forEach(card => {
          card.classList.add('animate-slide-in-left');
        });
        rightCards.forEach(card => {
          card.classList.add('animate-slide-in-right');
        });
      } else {
        group.classList.remove('active');
      }
    });
  }
}

// 初始化全局上传弹窗
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.aiUploadModal = new AiUploadModal();
    setupSafetyMechanism();
  });
} else {
  // DOM 已经加载完成，直接初始化
  window.aiUploadModal = new AiUploadModal();
  setupSafetyMechanism();
}

/**
 * 安全机制：防止抽屉永久锁定
 * 当上传弹窗关闭但抽屉仍被锁定时，自动解锁
 */
function setupSafetyMechanism() {
  // 检测弹窗是否可见
  function isModalVisible() {
    const modal = document.getElementById('ai-upload-modal');
    return modal && modal.classList.contains('active');
  }

  // 解锁所有抽屉的函数
  function unlockAllDrawers() {
    const allDrawers = document.querySelectorAll('quick-add-dialog');
    const allPopovers = document.querySelectorAll('quick-buy-popover');
    const cartDrawers = document.querySelectorAll('cart-drawer');

    let unlocked = false;

    allDrawers.forEach(drawer => {
      if (drawer.dataset.modalOpen === 'true' || drawer.dataset.aiUploadInProgress === 'true') {
        drawer.dataset.modalOpen = 'false';
        drawer.dataset.aiUploadInProgress = 'false';
        unlocked = true;
      }
    });
    allPopovers.forEach(popover => {
      if (popover.dataset.modalOpen === 'true' || popover.dataset.aiUploadInProgress === 'true') {
        popover.dataset.modalOpen = 'false';
        popover.dataset.aiUploadInProgress = 'false';
        unlocked = true;
      }
    });
    cartDrawers.forEach(drawer => {
      if (drawer.dataset.modalOpen === 'true' || drawer.dataset.aiUploadInProgress === 'true') {
        drawer.dataset.modalOpen = 'false';
        drawer.dataset.aiUploadInProgress = 'false';
        unlocked = true;
      }
    });

    if (unlocked) {
      console.log('[AiUploadModal Safety] Auto-unlocked drawers (cleared modalOpen and aiUploadInProgress)');
    }
  }

  // 定期检查：如果弹窗不可见但抽屉被锁定，自动解锁
  setInterval(() => {
    if (!isModalVisible()) {
      unlockAllDrawers();
    }
  }, 2000); // 每2秒检查一次

  // 监听页面可见性变化
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !isModalVisible()) {
      unlockAllDrawers();
    }
  });

  // 监听 ESC 键（用户可能按 ESC 关闭弹窗）
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !isModalVisible()) {
      unlockAllDrawers();
    }
  });

  console.log('[AiUploadModal Safety] Safety mechanism initialized');
}